import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TableSortLabel,
    TextField,
    Typography,
} from "@mui/material";
import CustomSnackbar from "../../components/CustomSnackbar";
import Logger from "../../../services/Logger";
import StatsSlidingService from "../../../services/StatsSlidingService";
import AdminSlidingService from "../AdminSlidingService";
import dayjs from "dayjs";

const LOW_PUBLISHED = 5;
const SORT_FIELDS = ["tag", "draftCount", "reviewedCount", "publishedCount", "playCount", "lastPublishedAt"];
const SORT_STORAGE_KEY = "admin.sliding.tagStats.sort";
const LANGUAGE_STORAGE_KEY = "admin.sliding.tagStats.language";
const DEFAULT_SORT = { field: "publishedCount", dir: "asc" };
const LANGUAGE_FILTER_OPTIONS = [
    { value: "", label: "All languages" },
    { value: "english", label: "English" },
    { value: "french", label: "French" },
];

function formatCount(value) {
    return Number.isFinite(Number(value)) ? Number(value).toLocaleString() : "—";
}

function formatDateTime(epochMillis) {
    return epochMillis ? dayjs(Number(epochMillis)).format("YYYY-MM-DD HH:mm") : "—";
}

function lastPublishedValue(row) {
    const raw = row?.lastPublishedAt;
    if (raw == null || raw === "") {
        return null;
    }
    const n = Number(raw);
    if (Number.isFinite(n) && n > 0) {
        return n;
    }
    const parsed = dayjs(raw);
    return parsed.isValid() ? parsed.valueOf() : null;
}

function loadStoredSort() {
    try {
        const raw = window.localStorage.getItem(SORT_STORAGE_KEY);
        if (!raw) {
            return DEFAULT_SORT;
        }
        const parsed = JSON.parse(raw);
        const field = SORT_FIELDS.includes(parsed?.field) ? parsed.field : DEFAULT_SORT.field;
        const dir = parsed?.dir === "desc" ? "desc" : "asc";
        return { field, dir };
    } catch {
        return DEFAULT_SORT;
    }
}

function persistSort(field, dir) {
    try {
        window.localStorage.setItem(SORT_STORAGE_KEY, JSON.stringify({ field, dir }));
    } catch {
        /* private mode */
    }
}

function loadLanguageFilter(fallback) {
    try {
        const v = window.localStorage.getItem(LANGUAGE_STORAGE_KEY);
        if (v === "" || v === "english" || v === "french") return v;
    } catch {
        /* private mode */
    }
    if (fallback === "" || fallback === "english" || fallback === "french") return fallback;
    return "english";
}

/**
 * Per-tag REVIEWED vs PUBLISHED sliding story counts, with publish-one for thin tags
 * and user play sessions on the last column.
 */
export default function SlidingTagStatsPanel({ language }) {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [rows, setRows] = useState([]);
    const [totals, setTotals] = useState({ draftTotal: 0, reviewedTotal: 0, publishedTotal: 0, playTotal: 0 });
    const [languageFilter, setLanguageFilter] = useState(() => loadLanguageFilter(language));
    const [keyword, setKeyword] = useState("");
    const [sort, setSort] = useState(loadStoredSort);
    const sortField = sort.field;
    const sortDir = sort.dir;
    const [publishTag, setPublishTag] = useState(null);
    const [publishing, setPublishing] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, severity: "info", message: "" });

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const result = await StatsSlidingService.statsByTag(languageFilter || undefined);
            const list = Array.isArray(result?.tags) ? result.tags : [];
            setRows(list);
            setTotals({
                draftTotal: Number(result?.draftTotal) || 0,
                reviewedTotal: Number(result?.reviewedTotal) || 0,
                publishedTotal: Number(result?.publishedTotal) || 0,
                playTotal: Number(result?.playTotal) || 0,
            });
        } catch (err) {
            Logger.error("Failed to load sliding tag stats:", err);
            setError("Failed to load tag statistics.");
            setRows([]);
        } finally {
            setLoading(false);
        }
    }, [languageFilter]);

    useEffect(() => {
        load();
    }, [load]);

    const handleSort = (field) => {
        if (!SORT_FIELDS.includes(field)) return;
        const nextDir =
            sortField === field
                ? (sortDir === "asc" ? "desc" : "asc")
                : (field === "lastPublishedAt" ? "desc" : "asc");
        setSort({ field, dir: nextDir });
        persistSort(field, nextDir);
    };

    const visibleRows = useMemo(() => {
        const q = keyword.trim().toLowerCase();
        const filtered = q
            ? rows.filter((row) => String(row.tag || "").toLowerCase().includes(q))
            : rows.slice();
        const dir = sortDir === "asc" ? 1 : -1;
        filtered.sort((a, b) => {
            if (sortField === "tag") {
                return dir * String(a.tag || "").localeCompare(String(b.tag || ""), undefined, {
                    sensitivity: "base",
                });
            }
            if (sortField === "lastPublishedAt") {
                const av = lastPublishedValue(a);
                const bv = lastPublishedValue(b);
                if (av == null && bv == null) {
                    return String(a.tag || "").localeCompare(String(b.tag || ""), undefined, {
                        sensitivity: "base",
                    });
                }
                if (av == null) return 1;
                if (bv == null) return -1;
                if (av !== bv) return dir * (av - bv);
                return String(a.tag || "").localeCompare(String(b.tag || ""), undefined, {
                    sensitivity: "base",
                });
            }
            const av = Number(a[sortField]) || 0;
            const bv = Number(b[sortField]) || 0;
            if (av !== bv) return dir * (av - bv);
            return String(a.tag || "").localeCompare(String(b.tag || ""), undefined, {
                sensitivity: "base",
            });
        });
        return filtered;
    }, [keyword, rows, sortDir, sortField]);

    const handlePublish = async () => {
        if (!publishTag || publishing) return;
        setPublishing(true);
        try {
            const result = await AdminSlidingService.publishReviewed(1, languageFilter || undefined, publishTag);
            if (!result.success) {
                setSnackbar({
                    open: true,
                    severity: "error",
                    message: result.error || "Publish failed",
                });
                return;
            }
            const count = result.data?.publishedCount ?? 0;
            setSnackbar({
                open: true,
                severity: count > 0 ? "success" : "warning",
                message:
                    count > 0
                        ? `Published ${count} sliding for “${publishTag}”.`
                        : `No REVIEWED sliding left for “${publishTag}”.`,
            });
            setPublishTag(null);
            await load();
        } catch (err) {
            Logger.error("Failed to publish sliding by tag:", err);
            setSnackbar({
                open: true,
                severity: "error",
                message: err?.message || "Publish failed",
            });
        } finally {
            setPublishing(false);
        }
    };

    if (loading) {
        return (
            <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return <Alert severity="error">{error}</Alert>;
    }

    return (
        <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Each row is one tag on a sliding story image. <strong>Draft</strong> is still in progress.
                <strong> Reviewed</strong> is the ready-to-go-live inventory. <strong>Published</strong> is live
                in the app. Publishing one reviewed image also queues a Sliding Puzzle Quest <strong>push</strong>{" "}
                (batched; sends only when server push is on). <strong>Last published</strong> is the
                most recent live bundle for that tag. <strong>Plays</strong> is how many user sessions
                have started that tag (guests included; test and staff excluded).
            </Typography>
            <Stack direction="row" spacing={2} sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
                <Typography variant="body2">
                    Tags: <strong>{formatCount(rows.length)}</strong>
                </Typography>
                <Typography variant="body2">
                    Draft: <strong>{formatCount(totals.draftTotal)}</strong>
                </Typography>
                <Typography variant="body2">
                    Reviewed: <strong>{formatCount(totals.reviewedTotal)}</strong>
                </Typography>
                <Typography variant="body2">
                    Published: <strong>{formatCount(totals.publishedTotal)}</strong>
                </Typography>
                <Typography variant="body2">
                    Plays: <strong>{formatCount(totals.playTotal)}</strong>
                </Typography>
            </Stack>
            <Stack direction="row" spacing={2} sx={{ mb: 2 }} flexWrap="wrap" useFlexGap alignItems="center">
                <FormControl size="small" sx={{ minWidth: 160 }}>
                    <InputLabel id="sliding-tag-stats-language-label">Language</InputLabel>
                    <Select
                        labelId="sliding-tag-stats-language-label"
                        value={languageFilter}
                        label="Language"
                        onChange={(event) => {
                            const next = event.target.value;
                            setLanguageFilter(next);
                            try {
                                window.localStorage.setItem(LANGUAGE_STORAGE_KEY, next);
                            } catch {
                                /* private mode */
                            }
                        }}
                    >
                        {LANGUAGE_FILTER_OPTIONS.map((option) => (
                            <MenuItem key={option.value || "all"} value={option.value}>
                                {option.label}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <TextField
                    size="small"
                    label="Filter tags"
                    value={keyword}
                    onChange={(event) => setKeyword(event.target.value)}
                    sx={{ maxWidth: 320, flex: "1 1 220px" }}
                />
            </Stack>
            <TableContainer>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>
                                <TableSortLabel
                                    active={sortField === "tag"}
                                    direction={sortField === "tag" ? sortDir : "asc"}
                                    onClick={() => handleSort("tag")}
                                >
                                    Tag
                                </TableSortLabel>
                            </TableCell>
                            <TableCell align="right">
                                <TableSortLabel
                                    active={sortField === "draftCount"}
                                    direction={sortField === "draftCount" ? sortDir : "asc"}
                                    onClick={() => handleSort("draftCount")}
                                >
                                    Draft
                                </TableSortLabel>
                            </TableCell>
                            <TableCell align="right">
                                <TableSortLabel
                                    active={sortField === "reviewedCount"}
                                    direction={sortField === "reviewedCount" ? sortDir : "asc"}
                                    onClick={() => handleSort("reviewedCount")}
                                >
                                    Reviewed
                                </TableSortLabel>
                            </TableCell>
                            <TableCell align="right">
                                <TableSortLabel
                                    active={sortField === "publishedCount"}
                                    direction={sortField === "publishedCount" ? sortDir : "asc"}
                                    onClick={() => handleSort("publishedCount")}
                                >
                                    Published
                                </TableSortLabel>
                            </TableCell>
                            <TableCell align="right">
                                <TableSortLabel
                                    active={sortField === "playCount"}
                                    direction={sortField === "playCount" ? sortDir : "asc"}
                                    onClick={() => handleSort("playCount")}
                                >
                                    Plays
                                </TableSortLabel>
                            </TableCell>
                            <TableCell>
                                <TableSortLabel
                                    active={sortField === "lastPublishedAt"}
                                    direction={sortField === "lastPublishedAt" ? sortDir : "desc"}
                                    onClick={() => handleSort("lastPublishedAt")}
                                >
                                    Last published
                                </TableSortLabel>
                            </TableCell>
                            <TableCell align="right">Publish</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {visibleRows.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={7}>
                                    <Typography variant="body2" color="text.secondary">
                                        No tags found.
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        ) : (
                            visibleRows.map((row) => {
                                const draft = Number(row.draftCount) || 0;
                                const reviewed = Number(row.reviewedCount) || 0;
                                const published = Number(row.publishedCount) || 0;
                                const plays = Number(row.playCount) || 0;
                                const low = published < LOW_PUBLISHED;
                                return (
                                    <TableRow key={row.tag} hover>
                                        <TableCell>
                                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                {row.tag}
                                            </Typography>
                                        </TableCell>
                                        <TableCell align="right">{formatCount(draft)}</TableCell>
                                        <TableCell align="right">{formatCount(reviewed)}</TableCell>
                                        <TableCell align="right">
                                            <Stack
                                                direction="row"
                                                spacing={1}
                                                justifyContent="flex-end"
                                                alignItems="center"
                                            >
                                                <span>{formatCount(published)}</span>
                                                {low ? (
                                                    <Chip
                                                        size="small"
                                                        color="warning"
                                                        variant="outlined"
                                                        label="Low"
                                                    />
                                                ) : null}
                                            </Stack>
                                        </TableCell>
                                        <TableCell align="right">{formatCount(plays)}</TableCell>
                                        <TableCell sx={{ whiteSpace: "nowrap" }}>
                                            {formatDateTime(lastPublishedValue(row))}
                                        </TableCell>
                                        <TableCell align="right">
                                            <Button
                                                size="small"
                                                variant="outlined"
                                                disabled={reviewed < 1}
                                                onClick={() => setPublishTag(row.tag)}
                                                sx={{ textTransform: "none" }}
                                            >
                                                Publish
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                );
                            })
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            <Dialog open={Boolean(publishTag)} onClose={() => !publishing && setPublishTag(null)}>
                <DialogTitle>Publish sliding</DialogTitle>
                <DialogContent>
                    <Typography variant="body2">
                        Publish 1 REVIEWED image for tag <strong>{publishTag}</strong>?
                        The newest-created puzzle is published first.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setPublishTag(null)} disabled={publishing}>
                        Cancel
                    </Button>
                    <Button onClick={handlePublish} disabled={publishing} variant="contained">
                        {publishing ? "Publishing…" : "Publish"}
                    </Button>
                </DialogActions>
            </Dialog>

            <CustomSnackbar
                open={snackbar.open}
                setOpen={(open) => setSnackbar((s) => ({ ...s, open }))}
                severity={snackbar.severity}
                message={snackbar.message}
            />
        </Box>
    );
}
